import type { AppProps } from "next/app";
import { FC, useMemo } from "react";
import { ThemeProvider } from "styled-components";
import { Provider as ReduxProvider } from "react-redux";
import { IntlProvider } from "react-intl";
import { Page } from "../../types/Page";
import { ApolloProvider } from "../apollo";
import { LaunchDarklyProvider } from "../launchdarkly";
import { MaterialProvider } from "../material";
import { Base } from "../modules/base";
import { Reset } from "../styles/Reset";
import { Global } from "../styles/Global";
import { theme } from "../styles/theme";
import { store } from "../redux";
import { Tokens } from "../styles/Tokens";
import { useShortLocale } from "../hooks/useShortLocale";
import "../../styles/globals.css";

import English from "../../content/compiled-locales/en.json";
import Hebrew from "../../content/compiled-locales/he.json";

const PunctClient: FC<
  AppProps & {
    Component: Page;
  }
> = ({ Component, pageProps }) => {
  const shortLocale = useShortLocale();

  const messages = useMemo(() => {
    switch (shortLocale) {
      case "en":
        return English;
      case "he":
        return Hebrew;
      default:
        return Hebrew;
    }
  }, [shortLocale]);

  return (
    <>
      <Reset />
      <Global />
      <Tokens />

      <MaterialProvider>
        <ThemeProvider theme={theme}>
          <ReduxProvider store={store}>
            <ApolloProvider>
              <LaunchDarklyProvider>
                <IntlProvider
                  locale={shortLocale}
                  messages={messages}
                  onError={console.error}
                >
                  <Base fullScreen={Component.fullScreen}>
                    <Component {...pageProps} />
                  </Base>
                </IntlProvider>
              </LaunchDarklyProvider>
            </ApolloProvider>
          </ReduxProvider>
        </ThemeProvider>
      </MaterialProvider>
    </>
  );
};

export default PunctClient;
